import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getMemberMobileAuthBridge } from '@/members-portal/lib/memberMobileAuthBridge'
import {
  getClubContextErrorKind,
  getStoredMemberClubCode,
  getStoredMemberOrg,
  isCapacitorRuntime,
  isValidClubCode,
  persistMemberClubContext,
  resolveAuthenticatedMemberClubCode,
  resolveMemberClubContextFromCode,
  setStoredMemberClubCode,
  isMemberLocallyLoggedOut,
} from './lib/mobileBootstrap'
import { markBootstrap } from './lib/bootstrapTelemetry'
import { getNativeCallbackState, setNativeCallbackState, useNativeCallbackState } from './lib/nativeAuthCallbackLifecycle'

const CLUB_CODE_LENGTH = 6
const MOBILE_AUTH_CALLBACK_INFLIGHT_KEY = 'privana_member_callback_inflight'
const MOBILE_CALLBACK_PATH = '/auth/callback'
const MOBILE_CALLBACK_ROUTE_PREFIX = `/capacitor/com.privana.members/callback`

function setMemberCallbackInflight(rawUrl) {
  if (typeof window === 'undefined') return
  const payload = {
    rawUrl: String(rawUrl || ''),
    timestamp: Date.now(),
  }
  window.sessionStorage?.setItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY, JSON.stringify(payload))
}

function resolveMemberCallbackPathFromUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null

  let parsed
  try {
    parsed = new URL(rawUrl)
  } catch {
    return null
  }

  const protocol = parsed.protocol.toLowerCase()
  if (protocol !== 'com.privana.members:') return null

  const host = (parsed.host || '').toLowerCase()
  const pathname = parsed.pathname || ''
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`

  const isLegacyAuthHostCallback = host === 'auth' && normalizedPath === '/callback'
  const isLegacyRootPathCallback = !host && normalizedPath === '/auth/callback'
  const isAuth0CapacitorCallback = Boolean(host) && normalizedPath === MOBILE_CALLBACK_ROUTE_PREFIX
  if (!isLegacyAuthHostCallback && !isLegacyRootPathCallback && !isAuth0CapacitorCallback) return null

  const callbackParams = new URLSearchParams(parsed.search || '')
  const hasAuth0CallbackPayload =
    callbackParams.has('state') &&
    (callbackParams.has('code') || callbackParams.has('error'))
  if (!hasAuth0CallbackPayload) return null

  return `${MOBILE_CALLBACK_PATH}${parsed.search || ''}${parsed.hash || ''}`
}

function clearBootstrapCallbackInflight() {
  if (typeof window === 'undefined') return
  window.sessionStorage?.removeItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
  window.localStorage?.removeItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
}

export function prepareMemberLoginLaunch() {
  if (typeof window === 'undefined') return false
  const callbackState = getNativeCallbackState().state
  const genuinelyActive = callbackState === 'awaiting_callback' || callbackState === 'processing_callback'
  const raw = window.sessionStorage?.getItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY) || window.localStorage?.getItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY)
  if (genuinelyActive) return false
  if (!raw) return true
  try {
    const parsed = JSON.parse(raw)
    const timestamp = Number(parsed?.timestamp || 0)
    console.info('[members-mobile-bootstrap]', { event: 'stale_callback_marker_cleared', markerStatus: String(parsed?.status || 'legacy'), markerExpired: !timestamp || Date.now() - timestamp > 10_000 })
  } catch {
    console.info('[members-mobile-bootstrap]', { event: 'stale_callback_marker_cleared', markerStatus: 'invalid', markerExpired: true })
  }
  clearBootstrapCallbackInflight()
  return true
}

function MobileBootstrapError({ message, detail, onRetry }) {
  return (
    <div className="mp-landing">
      <div className="mp-landing__card">
        <p className="mp-eyebrow">Members portal</p>
        <h2>We couldn&apos;t open your club portal</h2>
        <p>{message}</p>
        {detail ? <p>{detail}</p> : null}
        {onRetry ? <button type="button" className="mp-primary" onClick={onRetry}>Try secure sign-in again</button> : null}
      </div>
    </div>
  )
}

function ClubCodeEntryScreen({ onContinue, isSubmitting, errorMessage }) {
  const [digits, setDigits] = useState(Array(CLUB_CODE_LENGTH).fill(''))
  const inputRefs = useRef([])

  const clubCode = useMemo(() => digits.join(''), [digits])
  const isValid = useMemo(() => isValidClubCode(clubCode), [clubCode])

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const setDigitAtIndex = (index, value) => {
    setDigits((current) => {
      const next = [...current]
      next[index] = value
      return next
    })
  }

  const handleChange = (index, event) => {
    const value = String(event.target.value || '').replace(/\D/g, '')
    if (!value) {
      setDigitAtIndex(index, '')
      return
    }

    if (value.length > 1) {
      const nextDigits = Array(CLUB_CODE_LENGTH).fill('')
      value
        .slice(0, CLUB_CODE_LENGTH)
        .split('')
        .forEach((digit, digitIndex) => {
          nextDigits[digitIndex] = digit
        })
      setDigits(nextDigits)
      const targetIndex = Math.min(value.length, CLUB_CODE_LENGTH) - 1
      inputRefs.current[targetIndex]?.focus()
      return
    }

    setDigitAtIndex(index, value)
    if (index < CLUB_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      setDigitAtIndex(index - 1, '')
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (event.key === 'ArrowRight' && index < CLUB_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    const pasted = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '')
    if (!pasted) return

    event.preventDefault()
    const nextDigits = Array(CLUB_CODE_LENGTH).fill('')
    pasted
      .slice(0, CLUB_CODE_LENGTH)
      .split('')
      .forEach((digit, index) => {
        nextDigits[index] = digit
      })
    setDigits(nextDigits)
    const targetIndex = Math.min(pasted.length, CLUB_CODE_LENGTH) - 1
    inputRefs.current[Math.max(targetIndex, 0)]?.focus()
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!isValid || isSubmitting) return
    onContinue(clubCode)
  }

  return (
    <div className="mp-mobile-code">
      <div className="mp-mobile-code__panel">
        <p className="mp-mobile-code__eyebrow">PRIVANA</p>
        <h1 className="mp-mobile-code__title">Enter your club code</h1>
        <form className="mp-mobile-code__form" onSubmit={handleSubmit}>
          <div className="mp-mobile-code__inputs">
            {digits.map((digit, index) => (
              <input
                key={`club-code-${index}`}
                ref={(node) => {
                  inputRefs.current[index] = node
                }}
                className="mp-mobile-code__digit"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                autoComplete="one-time-code"
                aria-label={`Club code digit ${index + 1}`}
                value={digit}
                onChange={(event) => handleChange(index, event)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                onPaste={handlePaste}
                disabled={isSubmitting}
              />
            ))}
          </div>

          <p className="mp-mobile-code__helper">
            Your club will provide a 6-digit member access code
          </p>

          {errorMessage ? <p className="mp-mobile-code__error">{errorMessage}</p> : null}

          <button type="submit" className="mp-mobile-code__continue" disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Verifying…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

export { resolveMemberCallbackPathFromUrl }

export default function MobileAppBootstrap({ redirectUri }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0()
  const mobileAuthBridgeRef = useRef(getMemberMobileAuthBridge())
  const callbackLifecycle = useNativeCallbackState()
  const launchKeyRef = useRef(null)
  const [fullLogoutRequired] = useState(() => isMemberLocallyLoggedOut())
  const [resolveState, setResolveState] = useState({ status: 'idle', message: '' })
  const [callbackError, setCallbackError] = useState('')
  const [callbackErrorDetail, setCallbackErrorDetail] = useState('')
  const [pendingCallbackRoute, setPendingCallbackRoute] = useState(null)
  const [entryState, setEntryState] = useState(() => {
    const existingOrg = getStoredMemberOrg() || ''
    const existingClubCode = getStoredMemberClubCode() || null
    return {
      status: existingOrg && existingClubCode ? 'ready' : 'collecting',
      organization: existingOrg || null,
      clubCode: existingClubCode,
      error: location.state?.clubUnavailable
        ? 'This club is no longer available. Enter a club code to continue.'
        : '',
    }
  })

  useEffect(() => {
    markBootstrap('restored_club_code', { clubCode: getStoredMemberClubCode(), cache: getStoredMemberClubCode() ? 'hit' : 'miss' })
  }, [])

  useEffect(() => {
    if (!isLoading) markBootstrap('restored_auth0_session', { status: isAuthenticated ? 'authenticated' : 'unauthenticated' })
  }, [isAuthenticated, isLoading])

  useEffect(() => {
    if (location.state?.clubUnavailable) {
      navigate('/app', { replace: true, state: null })
    }
  }, [location.state, navigate])


  useEffect(() => {
    if (!pendingCallbackRoute) return
    if (isAuthenticated) {
      console.info('[members-mobile-auth] callback authenticated=true')
      navigate(pendingCallbackRoute, { replace: true })
      setPendingCallbackRoute(null)
      return
    }
    if (isLoading) return
    const timeout = window.setTimeout(() => {
      setCallbackError('We could not confirm your signed-in session. Please try signing in again.')
      setPendingCallbackRoute(null)
    }, 8000)
    return () => window.clearTimeout(timeout)
  }, [isAuthenticated, isLoading, navigate, pendingCallbackRoute])

  useEffect(() => {
    const gates = {
      authLoaded: !isLoading,
      authenticationAllowsLaunch: !isAuthenticated || fullLogoutRequired,
      entryReady: entryState.status === 'ready',
      organizationReady: Boolean(entryState.organization),
      callbackAvailable: prepareMemberLoginLaunch(),
      browserBridgeAvailable: typeof mobileAuthBridgeRef.current?.openBrowser === 'function',
    }
    console.info('[members-mobile-bootstrap]', { event: 'interactive_login_launch_evaluated', ...gates })
    if (!Object.values(gates).every(Boolean)) {
      if (entryState.status === 'ready' && !gates.callbackAvailable) {
        console.info('[members-mobile-bootstrap]', { event: 'authorization_launch_blocked', gate: 'active_callback' })
      } else if (entryState.status === 'ready' && !gates.browserBridgeAvailable) {
        setEntryState((current) => ({ ...current, status: 'launch_failed', error: 'We couldn’t open the secure sign-in page. Please try again.' }))
        console.info('[members-mobile-bootstrap]', { event: 'authorization_launch_failed', retryAvailable: true, gate: 'browser_bridge' })
      }
      return
    }

    const launchKey = `${entryState.organization}:interactive=${String(fullLogoutRequired)}`
    if (launchKeyRef.current === launchKey) return
    launchKeyRef.current = launchKey
    setEntryState((current) => ({ ...current, status: 'launching', error: '' }))

    const loginOptions = {
      authorizationParams: {
        redirect_uri: redirectUri,
        ui_mode: 'members',
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
        organization: entryState.organization,
        ...(fullLogoutRequired ? { prompt: 'login' } : {}),
      },
      appState: { mobileBootstrap: true, interactiveLoginRequired: fullLogoutRequired, clubCode: entryState.clubCode },
    }

    if (isCapacitorRuntime()) {
      loginOptions.openUrl = async (url) => {
        const bridge = mobileAuthBridgeRef.current
        console.info('[members-mobile-bootstrap]', { event: 'authorization_browser_presentation_requested', bridgeAvailable: typeof bridge?.openBrowser === 'function' })
        setNativeCallbackState('awaiting_callback', { startedAt: Date.now() })
        window.sessionStorage?.setItem(MOBILE_AUTH_CALLBACK_INFLIGHT_KEY, JSON.stringify({ timestamp: Date.now(), status: 'browser_opened' }))
        try {
          Promise.resolve(bridge.addBrowserFinishedListener?.(() => {
            if (getNativeCallbackState().state !== 'awaiting_callback') return
            clearBootstrapCallbackInflight()
            setNativeCallbackState('cancelled')
          })).catch(() => {})
          await bridge.openBrowser(url)
          console.info('[members-mobile-bootstrap]', { event: 'authorization_browser_awaiting_callback' })
        } catch (error) {
          clearBootstrapCallbackInflight()
          setNativeCallbackState('failed')
          throw error
        }
      }
    }

    console.info('[members-mobile-bootstrap]', { event: 'authorization_launch_requested', interactiveLoginRequired: fullLogoutRequired })
    Promise.resolve(loginWithRedirect(loginOptions)).catch(() => {
      clearBootstrapCallbackInflight()
      setNativeCallbackState('failed')
      launchKeyRef.current = null
      setEntryState((current) => ({ ...current, status: 'launch_failed', error: 'We couldn’t open the secure sign-in page. Please try again.' }))
      console.info('[members-mobile-bootstrap]', { event: 'authorization_launch_failed', retryAvailable: true })
    })
  }, [entryState.clubCode, entryState.organization, entryState.status, fullLogoutRequired, isAuthenticated, isLoading, loginWithRedirect, redirectUri])

  useEffect(() => {
    if (callbackLifecycle.state !== 'cancelled') return
    launchKeyRef.current = null
    setEntryState((current) => ({ ...current, status: 'launch_failed', error: 'Secure sign-in was cancelled. You can try again.' }))
  }, [callbackLifecycle.state])

  const handleClubCodeSubmit = async (clubCode) => {
    if (!['collecting', 'launch_failed'].includes(entryState.status)) return

    console.info('[members-mobile-bootstrap]', { event: 'club_code_submitted' })

    if (!isValidClubCode(clubCode)) {
      setEntryState((current) => ({
        ...current,
        error: 'Enter a valid 6-digit club code.',
      }))
      return
    }

    setEntryState((current) => ({
      ...current,
      status: 'resolving',
      error: '',
    }))

    try {
      console.info('[members-mobile-bootstrap]', { event: 'club_context_resolution_started' })
      const resolved = await resolveMemberClubContextFromCode(clubCode)
      persistMemberClubContext(resolved)
      console.info('[members-mobile-bootstrap]', { event: 'club_context_resolved', organizationPresent: Boolean(resolved.organization) })
      setEntryState({
        status: 'ready',
        organization: resolved.organization,
        clubCode: resolved.clubCode || clubCode,
        error: '',
      })
    } catch (error) {
      const errorKind = getClubContextErrorKind(error)
      console.error('[members-mobile-bootstrap] failed to resolve club context from code', {
        clubCode,
        error,
      })
      setEntryState((current) => ({
        ...current,
        status: 'collecting',
        error:
          errorKind === 'unavailable'
            ? 'That club code isn’t available for member login. Please check with your club.'
            : 'We couldn’t verify that code right now. Please try again in a moment.',
      }))
    }
  }

  useEffect(() => {
    if (!isAuthenticated || isMemberLocallyLoggedOut()) return
    let cancelled = false

    async function resolveClubRoute() {
      const storedClubCode = getStoredMemberClubCode()
      const storedOrganization = getStoredMemberOrg()
      console.info('[members-mobile-bootstrap] restore flow entered', {
        branch: 'authenticated_restore',
        isAuthenticated,
        pathname: window?.location?.pathname || null,
        storedClubCode,
        storedOrganization,
        hasUser: Boolean(user),
        userSub: user?.sub || null,
      })
      setResolveState({ status: 'resolving', message: '' })
      try {
        if (isValidClubCode(storedClubCode)) {
          console.info('[members-mobile-bootstrap] restore branch=use_stored_club_code', {
            clubCode: storedClubCode,
            navigateTo: `/${storedClubCode}/dashboard`,
          })
          if (!cancelled) {
            navigate(`/${storedClubCode}/dashboard`, { replace: true })
          }
          return
        }

        console.info('[members-mobile-bootstrap] restore branch=lookup_via_api_me', {
          storedClubCode: storedClubCode || null,
        })
        const clubCode = await resolveAuthenticatedMemberClubCode(getAccessTokenSilently, user)
        if (!clubCode || !isValidClubCode(clubCode)) {
          console.info('[members-mobile-bootstrap] restore branch=missing_valid_club_code', {
            resolvedClubCode: clubCode || null,
          })
          if (!cancelled) {
            setResolveState({
              status: 'error',
              message:
                'Your account is missing a valid club assignment. Please contact your club concierge for help.',
            })
          }
          console.error('[members-mobile-bootstrap] missing or invalid member club_code', {
            clubCode,
            userSub: user?.sub || null,
          })
          return
        }

        setStoredMemberClubCode(clubCode)
        console.info('[members-mobile-bootstrap] restore branch=navigate_dashboard', {
          clubCode,
          navigateTo: `/${clubCode}/dashboard`,
        })
        if (!cancelled) {
          navigate(`/${clubCode}/dashboard`, { replace: true })
        }
      } catch (error) {
        console.info('[members-mobile-bootstrap] restore branch=lookup_failed', {
          errorName: error?.name || null,
          errorMessage: error?.message || String(error),
        })
        if (!cancelled) {
          setResolveState({
            status: 'error',
            message:
              'We could not verify your club membership right now. Please try again in a moment.',
          })
        }
        console.error('[members-mobile-bootstrap] failed to resolve member club_code', error)
      }
    }

    resolveClubRoute()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, isAuthenticated, navigate, user])

  if (pendingCallbackRoute) {
    return <div className="mp-mobile-bootstrap-shell" aria-busy="true" aria-label="Finishing sign-in" />
  }

  if (callbackError) {
    return <MobileBootstrapError message={callbackError} detail={callbackErrorDetail} />
  }

  if (resolveState.status === 'error') {
    return <MobileBootstrapError message={resolveState.message} />
  }

  if (entryState.status === 'launch_failed') {
    return <MobileBootstrapError message={entryState.error} onRetry={() => {
      console.info('[members-mobile-bootstrap]', { event: 'authorization_retry_requested' })
      launchKeyRef.current = null
      setEntryState((current) => ({ ...current, status: 'ready', error: '' }))
    }} />
  }

  if (
    ((!isLoading && !isAuthenticated) || fullLogoutRequired) &&
    (entryState.status === 'collecting' || entryState.status === 'resolving')
  ) {
    return (
      <ClubCodeEntryScreen
        onContinue={handleClubCodeSubmit}
        isSubmitting={entryState.status === 'resolving'}
        errorMessage={entryState.error}
      />
    )
  }

  if (!isLoading && isAuthenticated) {
    return <div className="mp-mobile-bootstrap-shell" aria-busy="true" aria-label="Opening your club portal" />
  }

  if (isLoading) {
    return <div className="mp-mobile-bootstrap-shell" aria-busy="true" aria-label="Restoring session" />
  }

  return <div className="mp-mobile-bootstrap-shell" aria-busy="true" aria-label="Opening sign in" />
}
